import { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function getAuth() {
  try {
    return JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state ?? {};
  } catch {
    return {};
  }
}

function formatDate() {
  return new Date().toLocaleDateString('en-UG', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/menu': 'Menu Management',
  '/inventory': 'Inventory',
  '/hr': 'Human Resources',
  '/payroll': 'Payroll',
  '/finance': 'Finance',
  '/reports': 'Reports & Analytics',
  '/admin': 'Administration',
  '/pos-settings': 'POS Settings',
  '/crm': 'CRM & Loyalty',
  '/reservations': 'Reservations',
  '/delivery': 'Delivery',
  '/settings': 'Settings',
};

const NAV_GROUPS = [
  {
    label: 'Core',
    items: [
      {
        to: '/dashboard', label: 'Dashboard',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>,
      },
      {
        to: '/menu', label: 'Menu',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>,
      },
      {
        to: '/inventory', label: 'Inventory',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>,
      },
    ],
  },
  {
    label: 'People & Finance',
    items: [
      {
        to: '/hr', label: 'HR',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
      },
      {
        to: '/payroll', label: 'Payroll',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
      },
      {
        to: '/finance', label: 'Finance',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
      },
      {
        to: '/reports', label: 'Reports',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        to: '/crm', label: 'CRM & Loyalty',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>,
      },
      {
        to: '/reservations', label: 'Reservations',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
      },
      {
        to: '/delivery', label: 'Delivery',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" /></svg>,
      },
    ],
  },
  {
    label: 'System',
    items: [
      {
        to: '/pos-settings', label: 'POS Settings',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" /></svg>,
      },
      {
        to: '/admin', label: 'Admin',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
      },
      {
        to: '/settings', label: 'Settings',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>,
      },
    ],
  },
];

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} style={{ width: 18, height: 18 }}>
      <circle cx="12" cy="12" r="5" />
      <path strokeLinecap="round" d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} style={{ width: 18, height: 18 }}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} style={{ width: 18, height: 18 }}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function MenuHamburgerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} style={{ width: 20, height: 20 }}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} style={{ width: 20, height: 20 }}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} style={{ width: 15, height: 15 }}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  );
}

function NavGroup({ label, items, onNavClick }: { label: string; items: typeof NAV_GROUPS[0]['items']; onNavClick?: () => void }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <p style={{
        padding: '10px 18px 4px',
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'var(--p400)',
      }}>
        {label}
      </p>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavClick}
          className={({ isActive }) => isActive ? 'sidebar-nav-item active' : 'sidebar-nav-item'}
        >
          <span style={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {item.icon}
          </span>
          {item.label}
        </NavLink>
      ))}
    </div>
  );
}

export function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const auth = getAuth();
  const role = auth.role ?? 'User';
  const initials = role.slice(0, 2).toUpperCase();
  const pageTitle = PAGE_TITLES[location.pathname] ?? 'Dashboard';

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleLogout() {
    try {
      await fetch(`${API_BASE}/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${auth.token ?? ''}` },
      });
    } catch { /* ignore */ }
    localStorage.removeItem('urs-auth');
    navigate('/login', { replace: true });
  }

  const sidebarContent = (
    <>
      {/* Logo */}
      <div style={{
        padding: '16px 18px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
      }}>
        <div style={{
          width: 32, height: 32,
          background: 'linear-gradient(135deg, var(--p400), var(--p200))',
          borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} style={{ width: 16, height: 16 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C8 2 5 5 5 9c0 2.4 1.1 4.5 2.8 5.9L7 20h10l-.8-5.1C17.9 13.5 19 11.4 19 9c0-4-3-7-7-7z" />
          </svg>
        </div>
        <div>
          <p style={{ color: 'white', fontWeight: 700, fontSize: 14, lineHeight: 1, fontFamily: 'var(--font-title)' }}>URS</p>
          <p style={{ color: 'var(--p400)', fontSize: 10, lineHeight: 1, marginTop: 3 }}>Restaurant System</p>
        </div>
      </div>

      {/* Nav groups */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {NAV_GROUPS.map((g) => (
          <NavGroup
            key={g.label}
            label={g.label}
            items={g.items}
            onNavClick={() => setMobileOpen(false)}
          />
        ))}
      </nav>

      {/* User section */}
      <div style={{
        borderTop: '1px solid rgba(255,255,255,0.08)',
        padding: '12px 14px',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{
            width: 32, height: 32,
            background: 'var(--p600)',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: 'white', flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: 'white', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {role.replace(/_/g, ' ')}
            </p>
            <p style={{ color: 'var(--p400)', fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Demo Restaurant
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 10px',
            borderRadius: 8,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--p400)', fontSize: 12,
            transition: 'background 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.12)'; (e.currentTarget as HTMLButtonElement).style.color = '#f87171'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--p400)'; }}
        >
          <LogoutIcon />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="layout-root">
      {/* ── Desktop Sidebar ── */}
      <aside className="sidebar-desktop" style={{
        width: 220, flexShrink: 0,
        background: 'var(--p900)',
        display: 'flex', flexDirection: 'column',
        height: '100vh',
      }}>
        {sidebarContent}
      </aside>

      {/* ── Mobile overlay ── */}
      {mobileOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 40,
          }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Mobile off-canvas sidebar ── */}
      <aside style={{
        position: 'fixed', top: 0, left: 0, bottom: 0,
        width: 220,
        background: 'var(--p900)',
        display: 'flex', flexDirection: 'column',
        zIndex: 50,
        transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.25s ease',
      }} className="sidebar-mobile">
        {sidebarContent}
      </aside>

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100vh', overflow: 'hidden' }}>

        {/* ── Mobile topbar ── */}
        <header className="topbar-mobile" style={{
          height: 56,
          background: 'var(--p900)',
          display: 'flex', alignItems: 'center',
          padding: '0 16px', gap: 12,
          flexShrink: 0,
        }}>
          <button
            onClick={() => setMobileOpen(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', padding: 4 }}
          >
            <MenuHamburgerIcon />
          </button>
          <p style={{ color: 'white', fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-title)', flex: 1 }}>URS</p>
          <button
            onClick={toggleTheme}
            style={{
              width: 32, height: 32,
              background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer',
              borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white',
            }}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>
        </header>

        {/* ── Desktop topbar ── */}
        <header className="topbar-desktop" style={{
          height: 64,
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center',
          padding: '0 32px', gap: 16,
          flexShrink: 0,
        }}>
          {/* Page title + date */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{
              fontSize: 20, fontWeight: 700,
              fontFamily: 'var(--font-title)',
              color: 'var(--text)',
              lineHeight: 1,
              margin: 0,
            }}>
              {pageTitle}
            </h1>
            <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3, lineHeight: 1 }}>
              {formatDate()}
            </p>
          </div>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} style={{
              width: 14, height: 14,
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-3)',
              pointerEvents: 'none',
            }}>
              <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="m21 21-4.35-4.35" />
            </svg>
            <input
              placeholder="Search…"
              style={{
                paddingLeft: 30, paddingRight: 12,
                height: 34, width: 200,
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontSize: 13, color: 'var(--text)',
                outline: 'none',
                fontFamily: 'var(--font-sans)',
              }}
            />
          </div>

          {/* Bell */}
          <div ref={notifRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setNotifOpen((v) => !v)}
              style={{
                width: 36, height: 36,
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--text-2)',
              }}
            >
              <BellIcon />
            </button>
            {notifOpen && (
              <div style={{
                position: 'absolute', top: 42, right: 0,
                width: 280,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                zIndex: 100,
                padding: '12px 0',
              }}>
                <p style={{ padding: '0 16px 10px', fontWeight: 600, fontSize: 13, color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>
                  Notifications
                </p>
                <p style={{ padding: '16px', fontSize: 13, color: 'var(--text-3)', textAlign: 'center' }}>
                  No new notifications
                </p>
              </div>
            )}
          </div>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              width: 36, height: 36,
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'var(--text-2)',
            }}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          {/* User avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 34, height: 34,
              background: 'var(--accent)',
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: 'white',
            }}>
              {initials}
            </div>
            <div style={{ lineHeight: 1 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                {role.replace(/_/g, ' ')}
              </p>
              <p style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>Demo Restaurant</p>
            </div>
          </div>
        </header>

        {/* ── Page content ── */}
        <main style={{
          flex: 1,
          overflowY: 'auto',
          background: 'var(--bg)',
          padding: '0 32px 32px',
        }}>
          <Outlet />
        </main>

        {/* ── Footer ── */}
        <footer style={{
          background: 'var(--surface)',
          borderTop: '1px solid var(--border)',
          padding: '8px 32px',
          fontSize: 11,
          color: 'var(--text-3)',
          textAlign: 'center',
          flexShrink: 0,
        }}>
          Uganda Restaurant System &copy; {new Date().getFullYear()} &mdash; Built for the Ugandan market
        </footer>
      </div>

      {/* Mobile close button inside overlay area */}
      {mobileOpen && (
        <button
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', top: 14, left: 226, zIndex: 51,
            background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer',
            borderRadius: 8, width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white',
          }}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
}
